import React, { useState } from 'react';
import {
  Sliders,
  Hash,
  Eye,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  GripVertical,
  ExternalLink,
  Database,
  Tag,
} from 'lucide-react';
import type { BlockNode, VisibilityRule } from '../types';
import { BLOCK_MANIFESTS, type PropDefinition } from '../blocks/generated/manifest';

export interface TraitsManagerPanelProps {
  selectedNode: BlockNode;
  onUpdateProps: (nodeId: string, props: Record<string, unknown>) => void;
  onUpdateMeta?: (
    nodeId: string,
    meta: Partial<Pick<BlockNode, 'customName' | 'htmlId' | 'classes' | 'hidden' | 'locked' | 'visibility' | 'slotBinding'>>
  ) => void;
  pages?: Array<{ id: string; title: string; route_path: string }>;
}

export function TraitsManagerPanel({
  selectedNode,
  onUpdateProps,
  onUpdateMeta,
  pages = [],
}: TraitsManagerPanelProps): React.JSX.Element {
  const manifest = BLOCK_MANIFESTS[selectedNode.type as keyof typeof BLOCK_MANIFESTS];
  const [newClassInput, setNewClassInput] = useState('');
  const [expandedRepeaters, setExpandedRepeaters] = useState<Record<string, boolean>>({});

  const handlePropChange = (key: string, value: unknown) => {
    onUpdateProps(selectedNode.id, { [key]: value });
  };

  const handleMetaChange = (
    patch: Partial<Pick<BlockNode, 'customName' | 'htmlId' | 'classes' | 'hidden' | 'locked' | 'visibility' | 'slotBinding'>>
  ) => {
    if (onUpdateMeta) {
      onUpdateMeta(selectedNode.id, patch);
    }
  };

  const handleAddClass = () => {
    const trimmed = newClassInput.trim().replace(/\s+/g, '-');
    if (!trimmed) return;
    const existing = selectedNode.classes || [];
    if (!existing.includes(trimmed)) {
      handleMetaChange({ classes: [...existing, trimmed] });
    }
    setNewClassInput('');
  };

  const handleRemoveClass = (clsToRemove: string) => {
    const existing = selectedNode.classes || [];
    handleMetaChange({ classes: existing.filter((c) => c !== clsToRemove) });
  };

  const toggleRepeater = (key: string) => {
    setExpandedRepeaters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto no-scrollbar bg-card text-foreground select-none divide-y divide-border">
      {/* SECTION 1: HTML ATTRIBUTES & COMPONENT TRAITS */}
      <div className="p-3 flex flex-col gap-3">
        <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
          <Hash className="h-3.5 w-3.5 text-[#3b97e3]" />
          <span>Component Traits & Attributes</span>
        </div>

        {/* Custom Layer Label */}
        <div className="flex flex-col gap-1 text-[11px]">
          <label className="text-muted-foreground font-medium">Layer Title</label>
          <input
            type="text"
            value={selectedNode.customName || ''}
            placeholder={manifest?.title || selectedNode.type}
            onChange={(e) => handleMetaChange({ customName: e.target.value || undefined })}
            className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground focus:outline-none focus:border-[#3b97e3]"
          />
        </div>

        {/* HTML Anchor ID */}
        <div className="flex flex-col gap-1 text-[11px]">
          <label className="text-muted-foreground font-medium flex items-center justify-between">
            <span>HTML Anchor / ID</span>
            <span className="text-[10px] text-muted-foreground/70 font-mono">e.g. #courses</span>
          </label>
          <div className="flex items-center rounded border border-input bg-background overflow-hidden focus-within:border-[#3b97e3]">
            <span className="px-2 text-xs text-muted-foreground font-mono bg-muted/30">#</span>
            <input
              type="text"
              value={selectedNode.htmlId || ''}
              placeholder="section-id"
              onChange={(e) =>
                handleMetaChange({
                  htmlId: e.target.value.replace(/[^a-zA-Z0-9-_]/g, '') || undefined,
                })
              }
              className="w-full px-2 py-1 text-xs bg-transparent text-foreground font-mono focus:outline-none"
            />
          </div>
        </div>

        {/* Custom CSS Classes */}
        <div className="flex flex-col gap-1.5 text-[11px]">
          <label className="text-muted-foreground font-medium flex items-center gap-1">
            <Tag className="h-3 w-3" />
            <span>CSS Classes</span>
          </label>
          <div className="flex flex-wrap gap-1">
            {selectedNode.classes?.map((cls) => (
              <span
                key={cls}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono text-foreground"
              >
                .{cls}
                <button
                  type="button"
                  onClick={() => handleRemoveClass(cls)}
                  className="hover:text-rose-500 cursor-pointer"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={newClassInput}
              placeholder="add-class-name"
              onChange={(e) => setNewClassInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddClass();
                }
              }}
              className="flex-1 px-2 py-0.5 text-xs rounded border border-input bg-background text-foreground font-mono focus:outline-none"
            />
            <button
              type="button"
              onClick={handleAddClass}
              className="px-2 py-1 text-xs rounded bg-muted hover:bg-muted/80 text-foreground font-semibold cursor-pointer"
            >
              Add
            </button>
          </div>
        </div>
      </div>

      {/* SECTION 2: LIVE ERP DATA SLOT BINDING (if applicable) */}
      {((manifest as any)?.erpSlot || selectedNode.slotBinding) && (
        <div className="p-3 flex flex-col gap-2 bg-[#3b97e3]/5">
          <div className="flex items-center gap-1.5 text-xs font-bold text-[#3b97e3]">
            <Database className="h-3.5 w-3.5" />
            <span>Live ERP Data Slot</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Synchronized with Helios ERP {(manifest as any)?.erpSlot || selectedNode.slotBinding?.slotType}
          </p>
          <div className="flex items-center justify-between text-[11px] mt-1">
            <span className="text-muted-foreground">Refresh Interval</span>
            <span className="font-mono text-xs font-bold text-foreground">
              {selectedNode.slotBinding?.refreshIntervalSec || 300}s
            </span>
          </div>
        </div>
      )}

      {/* SECTION 3: MANIFEST-DEFINED CONTENT PROPS */}
      <div className="p-3 flex flex-col gap-3">
        <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
          <Sliders className="h-3.5 w-3.5 text-[#3b97e3]" />
          <span>Content & Properties</span>
        </div>

        {manifest?.props && Object.keys(manifest.props).length > 0 ? (
          Object.entries(manifest.props).map(([propKey, def]) => {
            const val = selectedNode.props?.[propKey];
            const pDef = def as PropDefinition;

            if (pDef.type === 'list' || pDef.control === 'list') {
              const items = Array.isArray(val) ? val : [];
              const isExpanded = expandedRepeaters[propKey] !== false;

              return (
                <div key={propKey} className="flex flex-col gap-2 pt-2 border-t border-border/60">
                  <button
                    type="button"
                    onClick={() => toggleRepeater(propKey)}
                    className="flex items-center justify-between text-xs font-bold text-foreground cursor-pointer"
                  >
                    <span>
                      {pDef.label || propKey} ({items.length})
                    </span>
                    {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  </button>

                  {isExpanded && (
                    <div className="flex flex-col gap-2 pl-2 border-l-2 border-primary/40">
                      {items.map((item: any, idx: number) => (
                        <div
                          key={idx}
                          className="p-2 rounded bg-muted/30 border border-border/60 flex flex-col gap-1.5 text-[11px]"
                        >
                          <div className="flex items-center justify-between font-semibold text-muted-foreground">
                            <span>Item #{idx + 1}</span>
                            <button
                              type="button"
                              onClick={() => {
                                const next = items.filter((_, i) => i !== idx);
                                handlePropChange(propKey, next);
                              }}
                              className="text-rose-500 hover:text-rose-600 cursor-pointer"
                              title="Delete Item"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>

                          {typeof item === 'object' && item !== null ? (
                            Object.entries(item).map(([subKey, subVal]) => (
                              <div key={subKey} className="flex flex-col gap-0.5">
                                <label className="text-[10px] text-muted-foreground font-mono">
                                  {subKey}
                                </label>
                                <input
                                  type="text"
                                  value={String(subVal ?? '')}
                                  onChange={(e) => {
                                    const next = [...items];
                                    next[idx] = { ...next[idx], [subKey]: e.target.value };
                                    handlePropChange(propKey, next);
                                  }}
                                  className="px-2 py-0.5 text-xs rounded border border-input bg-background text-foreground focus:outline-none"
                                />
                              </div>
                            ))
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              <input
                                type="text"
                                value={String(item ?? '')}
                                onChange={(e) => {
                                  const next = [...items];
                                  next[idx] = e.target.value;
                                  handlePropChange(propKey, next);
                                }}
                                className="px-2 py-0.5 text-xs rounded border border-input bg-background text-foreground focus:outline-none"
                              />
                            </div>
                          )}
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={() => {
                          if (items.length > 0 && typeof items[0] === 'object' && items[0] !== null) {
                            const template: Record<string, string> = {};
                            Object.keys(items[0]).forEach((k) => {
                              template[k] = '';
                            });
                            handlePropChange(propKey, [...items, template]);
                          } else {
                            handlePropChange(propKey, [...items, '']);
                          }
                        }}
                        className="px-2 py-1 text-xs font-semibold rounded border border-dashed border-primary/50 text-[#3b97e3] hover:bg-[#3b97e3]/10 flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <Plus className="h-3 w-3" /> Add {pDef.label || propKey} Item
                      </button>
                    </div>
                  )}
                </div>
              );
            }

            if (pDef.control === 'textarea' || pDef.type === 'richtext') {
              return (
                <div key={propKey} className="flex flex-col gap-1 text-[11px]">
                  <label className="text-muted-foreground font-medium">{pDef.label || propKey}</label>
                  <textarea
                    rows={3}
                    value={typeof val === 'string' ? val : ''}
                    onChange={(e) => handlePropChange(propKey, e.target.value)}
                    className="px-2 py-1.5 text-xs rounded border border-input bg-background text-foreground focus:outline-none focus:border-[#3b97e3]"
                  />
                </div>
              );
            }

            if ((pDef.control === 'select' || pDef.type === 'enum') && pDef.options) {
              return (
                <div key={propKey} className="flex flex-col gap-1 text-[11px]">
                  <label className="text-muted-foreground font-medium">{pDef.label || propKey}</label>
                  <select
                    value={typeof val === 'string' ? val : ''}
                    onChange={(e) => handlePropChange(propKey, e.target.value)}
                    className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground cursor-pointer focus:outline-none focus:border-[#3b97e3]"
                  >
                    {pDef.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }

            if (pDef.type === 'boolean' || pDef.control === 'switch') {
              return (
                <div key={propKey} className="flex items-center justify-between text-[11px] py-1">
                  <label className="text-muted-foreground font-medium">{pDef.label || propKey}</label>
                  <input
                    type="checkbox"
                    checked={Boolean(val)}
                    onChange={(e) => handlePropChange(propKey, e.target.checked)}
                    className="h-4 w-4 rounded border-input text-primary focus:ring-primary cursor-pointer"
                  />
                </div>
              );
            }

            if (pDef.type === 'number' || pDef.control === 'number') {
              return (
                <div key={propKey} className="flex flex-col gap-1 text-[11px]">
                  <label className="text-muted-foreground font-medium">{pDef.label || propKey}</label>
                  <input
                    type="number"
                    value={typeof val === 'number' ? val : ''}
                    onChange={(e) => handlePropChange(propKey, parseFloat(e.target.value) || 0)}
                    className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground focus:outline-none focus:border-[#3b97e3]"
                  />
                </div>
              );
            }

            // Default Text / String input
            return (
              <div key={propKey} className="flex flex-col gap-1 text-[11px]">
                <label className="text-muted-foreground font-medium">{pDef.label || propKey}</label>
                <input
                  type="text"
                  value={val !== undefined && val !== null ? String(val) : ''}
                  onChange={(e) => handlePropChange(propKey, e.target.value)}
                  className="px-2 py-1 text-xs rounded border border-input bg-background text-foreground focus:outline-none focus:border-[#3b97e3]"
                />
              </div>
            );
          })
        ) : (
          <div className="text-xs text-muted-foreground">
            No specific content fields for {selectedNode.type}.
          </div>
        )}
      </div>
    </div>
  );
}
